import axios, {AxiosError, AxiosInstance, AxiosRequestConfig, HttpStatusCode, InternalAxiosRequestConfig} from "axios";
import {AuthenticationRepository} from "./AuthenticationRepository.ts";
import {firstValueFrom} from "rxjs";
import HTTPConverter from "./converters/HTTPConverter.ts";
import generalErrorConverter from "./converters/GeneralErrorConverter.ts";
import {AgencyKit, AgencyKitSalesRepsResponse, AgencyKitsResponse, CreateAgencyKitPayload} from "../domain/entities/AgencyKit.ts";

const MIME_EXTENSIONS: Record<string, string> = {
    'image/png': 'png',
    'image/svg+xml': 'svg',
    'image/webp': 'webp',
    'image/jpeg': 'jpg',
}

// Admin-only: the sales team's "Kit comercial" screen (51trips-colab-api /agency-kits).
export class AgencyKitRepository {

    client: AxiosInstance

    get serverBaseURL(): string {
        return import.meta.env.VITE_APP_SERVER_URL
    }

    constructor(authenticationRepository: AuthenticationRepository) {
        this.client = axios.create({
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            // The create call uploads the logo and 5 mockups (~1–2 MB).
            timeout: 60000,
            validateStatus: (status: number) => status >= 200 && status < 300
        })

        this.client.interceptors.request.use(
            async (
                config: InternalAxiosRequestConfig
            ): Promise<InternalAxiosRequestConfig & { retry: number }> => {
                const token = await firstValueFrom(authenticationRepository.getIDToken())
                config.headers.set('Authorization', `Bearer ${token}`)

                const configWithRetry = config as InternalAxiosRequestConfig & {
                    retry: number
                }
                return { ...config, retry: configWithRetry.retry === 0 ? 0 : 1 }
            }
        )

        this.client.interceptors.response.use(
            (response) => HTTPConverter.fromResponse(response),
            async (err: AxiosError) => {
                const config = err.config as AxiosRequestConfig & { retry: number }
                if (err?.response?.status === HttpStatusCode.Unauthorized && config && config.retry > 0) {
                    config.retry = 0
                    return this.client.request(config)
                }
                return generalErrorConverter.fromBackendServerError(err)
            }
        )
    }

    getAgencyKits(agencyId?: string): Promise<AgencyKitsResponse> {
        return this.client.get(`${this.serverBaseURL}/agency-kits`, {params: {agencyId}})
    }

    getAgencyKitSalesReps(): Promise<AgencyKitSalesRepsResponse> {
        return this.client.get(`${this.serverBaseURL}/agency-kits/sales-reps`)
    }

    getAgencyKit(id: string): Promise<AgencyKit> {
        return this.client.get(`${this.serverBaseURL}/agency-kits/${id}`)
    }

    // The logo the kit was generated with (for "Duplicate and edit"); served by
    // the API because the bucket has no CORS for the portal.
    getAgencyKitLogo(id: string): Promise<Blob> {
        return this.client.get(`${this.serverBaseURL}/agency-kits/${id}/logo`, {responseType: 'blob'})
    }

    createAgencyKit(payload: CreateAgencyKitPayload): Promise<AgencyKit> {
        const form = new FormData()
        if (payload.agencyId) form.append('agencyId', payload.agencyId)
        form.append('agencyName', payload.agencyName)
        form.append('agencyColor', payload.agencyColor)
        form.append('preset', payload.preset)
        form.append('lang', payload.lang)
        form.append('salesRepId', payload.salesRepId)
        form.append('logo', payload.logo, `logo.${MIME_EXTENSIONS[payload.logo.type] ?? 'png'}`)
        form.append('logoWhite', payload.logoWhite, 'logo-white.png')
        payload.mockups.forEach(({id, ext, blob}) => form.append(`mockup_${id}`, blob, `${id}.${ext}`))
        form.append('includePricing', String(payload.includePricing))
        if (payload.pricing) {
            form.append('pricingCurrency', payload.pricing.currency)
            form.append('pricingPrices', payload.pricing.prices.join(','))
            if (payload.pricing.discountPercent != null) form.append('pricingDiscount', String(payload.pricing.discountPercent))
        }
        // Let the browser set the multipart boundary.
        return this.client.post(`${this.serverBaseURL}/agency-kits`, form, {headers: {'Content-Type': undefined}})
    }

    retryAgencyKit(id: string): Promise<AgencyKit> {
        return this.client.post(`${this.serverBaseURL}/agency-kits/${id}/retry`)
    }

    setAgencyKitActive(id: string, isActive: boolean): Promise<AgencyKit> {
        return this.client.patch(`${this.serverBaseURL}/agency-kits/${id}`, {isActive})
    }
}
